import { useAuth } from '../../modules/auth/context/AuthContext';
import { ROLES, type Role } from '../constants/roles';

/**
 * Hook que retorna a role do usuário logado
 * @returns A role do usuário logado ou null se não estiver autenticado
 */
export function useGetRole(): Role | null {
  const auth = useAuth();
  return auth.user?.role || null;
}

/**
 * Função utilitária que retorna a role do usuário logado
 * @param user - Objeto do usuário (opcional, se não fornecido usa o contexto)
 * @returns A role do usuário ou null se não estiver autenticado
 */
export function getRole(user?: any): Role | null {
  if (user?.role) {
    return user.role;
  }
  
  // Se não foi fornecido um usuário, tenta pegar do localStorage
  try {
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      return parsedUser?.role || null;
    }
  } catch (error) {
    console.error('Erro ao ler usuário do localStorage:', error);
  }
  
  return null;
}

/**
 * Hook que verifica se o usuário tem uma role específica
 * @param role - Role a ser verificada
 * @returns boolean indicando se o usuário tem a role especificada
 */
export function useHasRole(role: Role): boolean {
  const userRole = useGetRole();
  return userRole?.toLowerCase() === role.toLowerCase();
}

/**
 * Hook que verifica se o usuário é admin
 * @returns boolean indicando se o usuário é admin
 */
export function useIsAdmin(): boolean {
  return useHasRole(ROLES.ADMIN);
}

/**
 * Hook que verifica se o usuário é gestor
 * @returns boolean indicando se o usuário é gestor
 */
export function useIsGestor(): boolean {
  return useHasRole(ROLES.GESTOR);
}

/**
 * Hook que verifica se o usuário é usuário comum
 * @returns boolean indicando se o usuário é usuário comum
 */
export function useIsUser(): boolean {
  return useHasRole(ROLES.USER);
}

/**
 * Hook que verifica se o usuário é customer
 * @returns boolean indicando se o usuário é customer
 */
export function useIsCustomer(): boolean {
  return useHasRole(ROLES.CUSTOMER);
}
